import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import {
  FileText, CheckCircle, XCircle, Clock, Calendar,
  DollarSign, MapPin, User, Building2, Loader2,
  ArrowRight, Download, PenTool
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import config from '../config';

export const ContractPage = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (contractId) {
      fetchContract();
    } else {
      setError('شناسه قرارداد نامعتبر است');
      setLoading(false);
    }
  }, [contractId]);

  const fetchContract = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');

      const response = await fetch(config.getApiUrl(`/marketplace/contracts/${contractId}/`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Contract data:', data);
        setContract(data);
      } else if (response.status === 401) {
        toast.error('لطفاً وارد شوید');
        navigate('/login');
      } else if (response.status === 404) {
        setError('قرارداد یافت نشد');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'خطا در دریافت اطلاعات قرارداد');
      }
    } catch (error) {
      console.error('Error fetching contract:', error);
      setError('خطا در ارتباط با سرور');
    } finally {
      setLoading(false);
    }
  };

  const handleSignContract = async () => {
    if (!window.confirm('آیا از امضای این قرارداد اطمینان دارید؟')) return;

    setSigning(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/marketplace/contracts/${contractId}/sign/`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('قرارداد با موفقیت امضا شد');
        // به‌روزرسانی محلی قرارداد با داده‌های جدید
        setContract(prev => ({
          ...prev,
          customer_signed: data.customer_signed ?? prev.customer_signed,
          installer_signed: data.installer_signed ?? prev.installer_signed,
          status: data.status ?? prev.status
        }));
        // یا دوباره دریافت کنید
        fetchContract();
      } else {
        toast.error(data.error || 'خطا در امضای قرارداد');
      }
    } catch (error) {
      console.error('Error signing contract:', error);
      toast.error('خطا در ارتباط با سرور');
    } finally {
      setSigning(false);
    }
  };

  const handleDownloadContract = () => {
    if (!contract) return;

    const contractText = `
      قرارداد نصب سیستم خورشیدی
      ===========================
      
      شماره قرارداد: ${contract.id}
      تاریخ: ${new Date(contract.created_at).toLocaleDateString('fa-IR')}
      
      طرفین قرارداد:
      ---------------
      کارفرما: ${user?.username || 'مشتری'}
      پیمانکار: ${contract.bid?.installer?.company_name || 'نصاب'}
      
      موضوع قرارداد:
      ---------------
      نصب سیستم خورشیدی
      
      مبلغ قرارداد: ${contract.bid?.proposed_cost?.toLocaleString()} تومان
      
      شرایط:
      ------
      ${contract.terms}
      
      تاریخ امضای کارفرما: ${contract.customer_signed_at ? new Date(contract.customer_signed_at).toLocaleDateString('fa-IR') : '---'}
      تاریخ امضای پیمانکار: ${contract.installer_signed_at ? new Date(contract.installer_signed_at).toLocaleDateString('fa-IR') : '---'}
    `;

    const blob = new Blob([contractText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contract_${contract.id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('قرارداد با موفقیت دانلود شد');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
        <span className="mr-3 text-gray-600">در حال بارگذاری قرارداد...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900">خطا در بارگذاری</h2>
          <p className="text-red-500 mt-2">{error}</p>
          <div className="mt-6 flex gap-4 justify-center">
            <button onClick={fetchContract} className="bg-blue-600 text-white px-4 py-2 rounded-lg">
              تلاش مجدد
            </button>
            <Link to="/installations" className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50">
              بازگشت به درخواست‌ها
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900">قرارداد یافت نشد</h2>
          <Link to="/installations" className="mt-4 text-blue-600">بازگشت</Link>
        </div>
      </div>
    );
  }

  const isFullySigned = contract.customer_signed && contract.installer_signed;
  const canSign = !contract.customer_signed; // برای مشتری

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          <Link to="/installations" className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
            <ArrowRight className="w-4 h-4" /> بازگشت
          </Link>
        </div>

        {/* Contract Card */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold">قرارداد #{contract.id}</h1>
                <p className="text-blue-100 mt-1">تاریخ تنظیم: {new Date(contract.created_at).toLocaleDateString('fa-IR')}</p>
              </div>
              {isFullySigned ? (
                <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" /> تکمیل شده
                </span>
              ) : (
                <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" /> در انتظار امضا
                </span>
              )}
            </div>
          </div>

          {/* Parties Info */}
          <div className="p-6 border-b">
            <h2 className="text-lg font-bold mb-4">اطلاعات طرفین قرارداد</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <User className="w-5 h-5 text-blue-600" />
                  <span className="font-bold">کارفرما (مشتری)</span>
                </div>
                <p className="text-gray-700">{user?.username || 'مشتری'}</p>
                <p className="text-gray-500 text-sm">{user?.phone || '-'}</p>
                <div className="mt-3 flex items-center gap-2">
                  {contract.customer_signed ? (
                    <span className="text-green-600 text-sm flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> امضا شده
                    </span>
                  ) : (
                    <span className="text-gray-400 text-sm">در انتظار امضا</span>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  <span className="font-bold">پیمانکار (نصاب)</span>
                </div>
                <p className="text-gray-700">{contract.bid?.installer?.company_name || 'نصاب'}</p>
                <p className="text-gray-500 text-sm">{contract.bid?.installer?.license_number || '-'}</p>
                <div className="mt-3 flex items-center gap-2">
                  {contract.installer_signed ? (
                    <span className="text-green-600 text-sm flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> امضا شده
                    </span>
                  ) : (
                    <span className="text-gray-400 text-sm">در انتظار امضا</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Project Details */}
          <div className="p-6 border-b">
            <h2 className="text-lg font-bold mb-4">جزئیات پروژه</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <DollarSign className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">مبلغ قرارداد</p>
                  <p className="font-bold text-blue-600 text-lg">{contract.bid?.proposed_cost?.toLocaleString()} تومان</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">مدت زمان اجرا</p>
                  <p className="font-medium">{contract.bid?.proposed_duration || '?'} روز کاری</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">وضعیت</p>
                  <p className="font-medium">
                    {isFullySigned ? 'تکمیل شده و آماده اجرا' : 'در انتظار امضا'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Terms */}
          <div className="p-6 border-b">
            <h2 className="text-lg font-bold mb-4">شرایط و ضوابط</h2>
            <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
              <p className="text-gray-700 whitespace-pre-line">{contract.terms || 'شرایطی ثبت نشده است'}</p>
            </div>
          </div>

          {/* Signature Actions */}
          <div className="p-6 flex flex-wrap justify-between items-center gap-4">
            <button
              onClick={handleDownloadContract}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Download className="w-4 h-4" />
              دانلود قرارداد
            </button>
            
            {/* در بخش Signature Actions، بعد از دکمه دانلود */}
            {isFullySigned && user?.role === 'installer' && (
              <Link
                to={`/installer/schedule/${contractId}`}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 flex items-center gap-2"
              >
                <Calendar className="w-5 h-5" />
                زمان‌بندی نصب
              </Link>
            )}

            {/* دکمه امضا برای مشتری */}
            {canSign && (
              <button
                onClick={handleSignContract}
                disabled={signing}
                className="bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {signing ? <Loader2 className="w-5 h-5 animate-spin" /> : <PenTool className="w-5 h-5" />}
                امضای قرارداد
              </button>
            )}

            {/* دکمه پیگیری پروژه - نمایش در هر صورت بعد از امضای کامل */}
            {isFullySigned && (
              <Link
                to={`/project/track/${contractId}`}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 flex items-center gap-2"
              >
                پیگیری پروژه
                <ArrowRight className="w-4 h-4" />
              </Link>
            )}

            {/* اگر فقط نصاب امضا کرده باشد */}
            {contract.installer_signed && !contract.customer_signed && (
              <div className="text-yellow-600 text-sm">
                در انتظار امضای شما (مشتری)
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
